import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Upload,
  Typography,
  Alert,
  Tooltip,
  Popconfirm,
  App,
  Spin,
} from 'antd';
import {
  UploadOutlined,
  SafetyCertificateOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  FileProtectOutlined,
  LockOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile, RcFile } from 'antd/es/upload/interface';
import dayjs from 'dayjs';
import {
  useGetCertificadosQuery,
  useSubirCertificadoMutation,
  useActivarCertificadoMutation,
  useEliminarCertificadoMutation,
} from '../../services/facturacionApi';
import { CertificadoInfo } from '../../types/facturacion';
import { useResponsive } from '../../hooks/useResponsive';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import { getErrorMessage } from '../../utils/errorHelpers';

const { Title, Text } = Typography;

const CertificadosManager: React.FC = () => {
  const { isMobile } = useResponsive();
  const { formatInTimeZone } = useRestaurantTimeZone();
  const { message } = App.useApp();
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [cerFile, setCerFile] = useState<UploadFile | null>(null);
  const [keyFile, setKeyFile] = useState<UploadFile | null>(null);

  // API hooks
  const { data: certificados = [], isLoading, refetch } = useGetCertificadosQuery();
  const [subirCertificado, { isLoading: subiendo }] = useSubirCertificadoMutation();
  const [activarCertificado, { isLoading: activando }] = useActivarCertificadoMutation();
  const [eliminarCertificado, { isLoading: eliminando }] = useEliminarCertificadoMutation();

  const handleOpenModal = () => {
    form.resetFields();
    setCerFile(null);
    setKeyFile(null);
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    form.resetFields();
    setCerFile(null);
    setKeyFile(null);
  };

  const fileToBase64 = (file: RcFile): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onload = () => {
        const bytes = new Uint8Array(reader.result as ArrayBuffer);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        const base64 = btoa(binary);
        if (!base64) {
          reject(new Error('El archivo está vacío'));
          return;
        }
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();

      if (!cerFile || !keyFile) {
        message.error('Debe seleccionar el archivo .cer y .key');
        return;
      }

      const cerBase64 = await fileToBase64(cerFile as unknown as RcFile);
      const keyBase64 = await fileToBase64(keyFile as unknown as RcFile);
      const password = form.getFieldValue('password');
      const alias = form.getFieldValue('alias');
      const notas = form.getFieldValue('notas');

      await subirCertificado({
        cerBase64,
        keyBase64,
        password,
        activo: true,
        alias,
        notas,
      }).unwrap();

      message.success('Certificado subido exitosamente');
      handleCloseModal();
      refetch();
    } catch (error: unknown) {
      console.error('Error al subir certificado:', error);
      const errorMsg = getErrorMessage(error, 'Error al subir el certificado');
      message.error(errorMsg, 8);
    }
  };

  const handleActivar = async (id: number) => {
    try {
      await activarCertificado(id).unwrap();
      message.success('Certificado activado exitosamente');
      refetch();
    } catch (error: unknown) {
      console.error('Error al activar certificado:', error);
      const errorMsg = getErrorMessage(error, 'Error al activar el certificado');
      message.error(errorMsg, 8);
    }
  };

  const handleEliminar = async (id: number) => {
    try {
      await eliminarCertificado(id).unwrap();
      message.success('Certificado eliminado exitosamente');
      refetch();
    } catch (error: unknown) {
      console.error('Error al eliminar certificado:', error);
      const errorMsg = getErrorMessage(error, 'Error al eliminar el certificado');
      message.error(errorMsg, 8);
    }
  };

  const getStatusTag = (certificado: CertificadoInfo) => {
    const now = dayjs();
    const vigenciaFin = dayjs(certificado.vigenciaFin);
    const isExpired = vigenciaFin.isBefore(now);
    const isExpiringSoon = vigenciaFin.diff(now, 'day') <= 30;

    if (isExpired) {
      return <Tag color="error">Vencido</Tag>;
    }
    if (!certificado.activo) {
      return <Tag color="default">Inactivo</Tag>;
    }
    if (isExpiringSoon) {
      return <Tag color="warning">Por vencer</Tag>;
    }
    return <Tag color="success">Activo</Tag>;
  };

  const columns: ColumnsType<CertificadoInfo> = [
    {
      title: 'Certificado',
      key: 'certificado',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>
            <SafetyCertificateOutlined style={{ marginRight: 8 }} />
            {record.numeroCertificado}
          </div>
          <div style={{ fontSize: 12, color: 'var(--color-neutral-text)' }}>
            RFC: {record.rfc}
          </div>
          {record.alias && (
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              {record.alias}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Vigencia',
      key: 'vigencia',
      render: (_, record) => (
        <div>
          <div style={{ fontSize: 12 }}>
            <Text type="secondary">Desde: </Text>
            {formatInTimeZone(record.vigenciaInicio, 'DD/MM/YYYY')}
          </div>
          <div style={{ fontSize: 12 }}>
            <Text type="secondary">Hasta: </Text>
            {formatInTimeZone(record.vigenciaFin, 'DD/MM/YYYY')}
          </div>
        </div>
      ),
    },
    {
      title: 'Estado',
      key: 'estado',
      render: (_, record) => getStatusTag(record),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: isMobile ? 100 : 180,
      render: (_, record) => {
        const now = dayjs();
        const vigenciaFin = dayjs(record.vigenciaFin);
        const isExpired = vigenciaFin.isBefore(now);

        return (
          <Space size="small" wrap>
            {!record.activo && !isExpired && (
              <Tooltip title="Activar certificado">
                <Button
                  type="text"
                  size="small"
                  icon={<CheckCircleOutlined />}
                  onClick={() => handleActivar(record.id)}
                  loading={activando}
                  style={{ color: '#52c41a' }}
                />
              </Tooltip>
            )}
            <Popconfirm
              title="Eliminar certificado"
              description="¿Está seguro de eliminar este certificado?"
              onConfirm={() => handleEliminar(record.id)}
              okText="Sí"
              cancelText="No"
            >
              <Tooltip title="Eliminar certificado">
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  loading={eliminando}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={5} style={{ margin: 0 }}>
          <FileProtectOutlined style={{ marginRight: 8 }} />
          Certificados de Sello Digital (CSD)
        </Title>
        <Button
          type="primary"
          icon={<UploadOutlined />}
          onClick={handleOpenModal}
        >
          {isMobile ? 'Subir' : 'Subir Certificado'}
        </Button>
      </div>

      <Alert
        message="Los certificados CSD son necesarios para timbrar facturas"
        description="Suba su archivo .cer y .key proporcionados por el SAT. Solo puede haber un certificado activo a la vez."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
        </div>
      ) : certificados.length === 0 ? (
        <Card>
          <div style={{ textAlign: 'center', padding: 40 }}>
            <SafetyCertificateOutlined style={{ fontSize: 48, color: 'var(--color-neutral-text)', marginBottom: 16 }} />
            <Title level={5} style={{ color: 'var(--color-neutral-text)' }}>No hay certificados registrados</Title>
            <Text type="secondary">
              Suba su certificado CSD para poder timbrar facturas
            </Text>
          </div>
        </Card>
      ) : (
        <Table
          columns={columns}
          dataSource={certificados}
          rowKey="id"
          pagination={false}
          size="small"
          scroll={{ x: isMobile ? 500 : undefined }}
        />
      )}

      {/* Modal para subir certificado */}
      <Modal
        title={
          <Space>
            <SafetyCertificateOutlined />
            Subir Certificado CSD
          </Space>
        }
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={handleCloseModal}
        confirmLoading={subiendo}
        okText="Subir Certificado"
        cancelText="Cancelar"
        width={isMobile ? '100%' : 500}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Archivo .cer"
            required
            help="Seleccione el archivo de certificado (.cer)"
          >
            <Upload
              accept=".cer"
              maxCount={1}
              beforeUpload={(file) => {
                setCerFile(file as unknown as UploadFile);
                return false; // Prevent auto upload
              }}
              onRemove={() => setCerFile(null)}
              fileList={cerFile ? [cerFile] : []}
            >
              <Button icon={<UploadOutlined />}>
                Seleccionar archivo .cer
              </Button>
            </Upload>
          </Form.Item>

          <Form.Item
            label="Archivo .key"
            required
            help="Seleccione el archivo de llave privada (.key)"
          >
            <Upload
              accept=".key"
              maxCount={1}
              beforeUpload={(file) => {
                setKeyFile(file as unknown as UploadFile);
                return false; // Prevent auto upload
              }}
              onRemove={() => setKeyFile(null)}
              fileList={keyFile ? [keyFile] : []}
            >
              <Button icon={<UploadOutlined />}>
                Seleccionar archivo .key
              </Button>
            </Upload>
          </Form.Item>

          <Form.Item
            name="password"
            label="Contraseña del certificado"
            rules={[{ required: true, message: 'Ingrese la contraseña del certificado' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Contraseña de la llave privada"
            />
          </Form.Item>

          <Form.Item
            name="alias"
            label="Alias (opcional)"
          >
            <Input placeholder="Ej: Certificado Principal" />
          </Form.Item>

          <Form.Item
            name="notas"
            label="Notas (opcional)"
          >
            <Input.TextArea rows={2} placeholder="Notas adicionales..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CertificadosManager;
